import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import CloseIcon from '@mui/icons-material/Close';
import MergeIcon from '@mui/icons-material/CallMerge';

/**
 * Modal que se muestra antes de fusionar un grupo de elementos (imágenes o
 * preguntas). Lista todas las unidades de aprendizaje presentes en el grupo
 * (deduplicadas) con un checkbox cada una, para elegir cuáles tendrá el elemento
 * conservado tras la fusión. Por defecto vienen todas marcadas para no perder
 * ninguna asociación.
 *
 * Los textos se parametrizan con props (`entitySingular`, `entityPlural`,
 * `deletedLabel`, `referencesLabel`) para reutilizarlo entre imágenes y
 * preguntas; por defecto usa la redacción de imágenes.
 */
function MergeLearnUnitsModal({
  open,
  onClose,
  learnUnits = [],
  keepId,
  deleteCount,
  merging = false,
  onConfirm,
  entitySingular = 'imagen',
  entityPlural = 'imágenes',
  deletedLabel = 'imagen(es) duplicada(s)',
  referencesLabel = 'preguntas, laminarios, quizzes, etc.',
}) {
  const [selected, setSelected] = useState([]);

  // Al abrir el modal (o cambiar el grupo), pre-seleccionar todas las unidades.
  useEffect(() => {
    if (open) {
      setSelected(learnUnits.map(unit => unit.id));
    }
  }, [open, learnUnits]);

  const toggle = id => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(unitId => unitId !== id) : [...prev, id]
    );
  };

  const allChecked = learnUnits.length > 0 && selected.length === learnUnits.length;
  const someChecked = selected.length > 0 && !allChecked;

  const toggleAll = () => {
    setSelected(allChecked ? [] : learnUnits.map(unit => unit.id));
  };

  return (
    <Dialog open={open} onClose={merging ? undefined : onClose} maxWidth='xs' fullWidth>
      <DialogTitle>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Stack direction='row' spacing={1} alignItems='center'>
            <MergeIcon />
            <Typography variant='h6' fontWeight='bold'>
              Fusionar {entityPlural}
            </Typography>
          </Stack>
          <IconButton
            edge='end'
            color='inherit'
            onClick={onClose}
            aria-label='close'
            disabled={merging}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant='body2' sx={{ mb: 2 }}>
          Se conservará la {entitySingular} <strong>#{keepId}</strong> y se eliminarán{' '}
          <strong>{deleteCount}</strong> {deletedLabel}. Las referencias ({referencesLabel}) se
          reasignarán a la {entitySingular} conservada. Esta acción no se puede deshacer.
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Typography variant='subtitle2' sx={{ mb: 1 }}>
          Unidades de aprendizaje de la {entitySingular} conservada
        </Typography>
        {learnUnits.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            Ninguna de las {entityPlural} del grupo tiene unidades de aprendizaje asociadas.
          </Typography>
        ) : (
          <>
            <Typography variant='caption' color='text.secondary' sx={{ mb: 1, display: 'block' }}>
              Marca las unidades que tendrá la {entitySingular} tras la fusión (las no marcadas se
              desasociarán de ella).
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  size='small'
                  checked={allChecked}
                  indeterminate={someChecked}
                  onChange={toggleAll}
                />
              }
              label={
                <Typography variant='body2' fontWeight='bold'>
                  Seleccionar todas
                </Typography>
              }
            />
            <Divider sx={{ my: 1 }} />
            <FormGroup>
              {learnUnits.map(unit => (
                <FormControlLabel
                  key={unit.id}
                  control={
                    <Checkbox
                      size='small'
                      checked={selected.includes(unit.id)}
                      onChange={() => toggle(unit.id)}
                    />
                  }
                  label={<Typography variant='body2'>{unit.name}</Typography>}
                />
              ))}
            </FormGroup>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={merging}>
          Cancelar
        </Button>
        <Button
          variant='contained'
          startIcon={<MergeIcon />}
          disabled={merging}
          onClick={() => onConfirm(selected)}>
          {merging ? 'Fusionando…' : 'Fusionar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MergeLearnUnitsModal;
